import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "issue-form" }
const _hoisted_2 = { class: "issue-form__grid" }
const _hoisted_3 = { class: "app-form-field app-form-field--full" }
const _hoisted_4 = { class: "app-form-field__label app-form-field__label--required" }
const _hoisted_5 = { class: "app-form-field__wrapper" }
const _hoisted_6 = ["placeholder"]
const _hoisted_7 = {
  key: 0,
  class: "app-form-field__error"
}
const _hoisted_8 = { class: "app-form-field app-form-field--full" }
const _hoisted_9 = { class: "app-form-field__label" }
const _hoisted_10 = { class: "app-form-field__wrapper" }
const _hoisted_11 = ["placeholder"]
const _hoisted_12 = { class: "app-form-field app-form-field--full" }
const _hoisted_13 = { class: "issue-form__row" }
const _hoisted_14 = { class: "app-form-field" }
const _hoisted_15 = { class: "app-form-field__label app-form-field__label--required" }
const _hoisted_16 = { class: "app-form-field__wrapper" }
const _hoisted_17 = { value: "LOW" }
const _hoisted_18 = { value: "MEDIUM" }
const _hoisted_19 = { value: "HIGH" }
const _hoisted_20 = { value: "CRITICAL" }
const _hoisted_21 = { class: "app-form-field app-form-field--full" }
const _hoisted_22 = { class: "issue-form__row" }
const _hoisted_23 = { class: "app-form-field" }
const _hoisted_24 = { class: "app-form-field__label" }
const _hoisted_25 = { class: "app-form-field__wrapper" }
const _hoisted_26 = { value: null }
const _hoisted_27 = ["value"]
const _hoisted_28 = { class: "app-form-field app-form-field--full" }
const _hoisted_29 = { class: "issue-form__row" }
const _hoisted_30 = {
  key: 0,
  class: "app-form-field"
}
const _hoisted_31 = { class: "app-form-field__label" }
const _hoisted_32 = { class: "app-form-field__wrapper" }
const _hoisted_33 = ["min"]
const _hoisted_34 = {
  key: 0,
  class: "app-form-field app-form-field--full"
}
const _hoisted_35 = { class: "app-form-field__wrapper" }
const _hoisted_36 = ["placeholder"]
const _hoisted_37 = { class: "app-form-field app-form-field--full" }
const _hoisted_38 = { class: "app-form-field__label" }
const _hoisted_39 = { class: "photo-upload-text" }
const _hoisted_40 = { class: "photo-upload-hint" }
const _hoisted_41 = {
  key: 0,
  class: "photos-preview"
}
const _hoisted_42 = ["src", "alt"]
const _hoisted_43 = { class: "photo-preview-info" }
const _hoisted_44 = { class: "photo-preview-name" }
const _hoisted_45 = { class: "photo-preview-size" }
const _hoisted_46 = ["onClick", "title"]
const _hoisted_47 = {
  key: 1,
  class: "upload-progress"
}
const _hoisted_48 = { class: "upload-progress-bar" }
const _hoisted_49 = { class: "upload-progress-text" }
const _hoisted_50 = {
  key: 0,
  class: "issue-form__error"
}
const _hoisted_51 = { class: "issue-form__actions" }

import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';

// Components
import AppModal from '../../base/Modal.vue';
import AppButton from '../../base/AppButton.vue';

// Services
import deliveryIssuesService from '@/services/deliveryIssues.service';
import issuePhotoService, { type IssuePhoto } from '@/services/issuePhoto.service';
import type { Issue, IssueStatus, IssuePriority } from '@/types/issue';

interface SelectedPhoto {
  file: File;
  preview: string;
}

interface Intervenant {
  id: number;
  name: string;
}

interface Props {
  visible: boolean;
  issue: Issue | null;
  projectId: string | number;
  intervenants: Intervenant[];
}

interface Emits {
  close: [];
  'issue-saved': [issue: Issue, isNew: boolean];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'IssueFormModal',
  props: {
    visible: { type: Boolean },
    issue: {},
    projectId: {},
    intervenants: {}
  },
  emits: ["close", "issue-saved"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;
const emit = __emit;

const { t } = useI18n();
const { notifyError } = useNotifications();

// Form data
const formData = ref({
  title: '',
  description: '',
  status: 'OPEN' as IssueStatus,
  priority: 'MEDIUM' as IssuePriority,
  createdAt: new Date().toISOString().split('T')[0],
  resolvedDate: '',
  assignedTo: null as number | null,
  resolutionNotes: ''
});

const errors = ref<Record<string, string>>({});
const saving = ref(false);

// Photo upload state
const fileInput = ref<HTMLInputElement | null>(null);
const selectedPhotos = ref<SelectedPhoto[]>([]);
const isDragging = ref(false);
const uploadingPhotos = ref(false);
const uploadProgress = ref(0);

// Computed
const isEditing = computed(() => props.issue !== null);

const showResolvedDate = computed(() => {
  return formData.value.status === 'RESOLVED' || formData.value.status === 'CLOSED';
});

const showResolutionNotes = computed(() => {
  return formData.value.status === 'RESOLVED' || formData.value.status === 'CLOSED';
});

const isFormValid = computed(() => {
  return formData.value.title.trim().length >= 3;
});

// Methods
const loadFormData = () => {
  if (props.issue) {
    formData.value = {
      title: props.issue.title || '',
      description: props.issue.description || '',
      status: props.issue.status || 'OPEN',
      priority: props.issue.priority || 'MEDIUM',
      resolvedDate: props.issue.resolvedDate || '',
      assignedTo: props.issue.assignedTo || null,
      resolutionNotes: props.issue.resolutionNotes || ''
    };
  } else {
    formData.value = {
      title: '',
      description: '',
      status: 'OPEN',
      priority: 'MEDIUM',
      createdAt: new Date().toISOString().split('T')[0],
      resolvedDate: '',
      assignedTo: null,
      resolutionNotes: ''
    };
  }
  errors.value = {};
};

const validateForm = (): boolean => {
  errors.value = {};

  if (!formData.value.title || formData.value.title.trim().length < 3) {
    errors.value.title = t('issues.errors.titleTooShort');
    return false;
  }

  if (formData.value.title.length > 100) {
    errors.value.title = t('issues.errors.titleTooLong');
    return false;
  }
  
  if (formData.value.status === 'RESOLVED' && !formData.value.resolutionNotes) {
    errors.value.resolutionNotes = t('issues.errors.resolutionNotesRequired');
    return false;
  }

  return true;
};

// Photo upload methods
const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    addFiles(Array.from(target.files));
  }
};

const handleFileDrop = (event: DragEvent) => {
  isDragging.value = false;
  if (event.dataTransfer?.files) {
    const imageFiles = Array.from(event.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    addFiles(imageFiles);
  }
};

const addFiles = (files: File[]) => {
  files.forEach(file => {
    // Create preview URL
    const preview = URL.createObjectURL(file);
    selectedPhotos.value.push({ file, preview });
  });
};

const removePhoto = (index: number) => {
  // Revoke the preview URL to free memory
  URL.revokeObjectURL(selectedPhotos.value[index].preview);
  selectedPhotos.value.splice(index, 1);
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const uploadPhotos = async (issueId: number | string): Promise<void> => {
  if (selectedPhotos.value.length === 0) return;

  uploadingPhotos.value = true;
  uploadProgress.value = 0;

  try {
    const formData = new FormData();
    selectedPhotos.value.forEach((photo: SelectedPhoto) => {
      formData.append('photos', photo.file);
    });

    await issuePhotoService.uploadIssuePhotos(
      props.projectId,
      issueId,
      formData,
      (progressEvent) => {
        if (progressEvent.total) {
          uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      }
    );

    // Clear selected photos after successful upload
    selectedPhotos.value.forEach((photo: SelectedPhoto) => URL.revokeObjectURL(photo.preview));
    selectedPhotos.value = [];
  } catch (error: any) {
    console.error('Error uploading photos:', error);
    notifyError(t('issues.errors.photoUploadFailed'));
    throw error;
  } finally {
    uploadingPhotos.value = false;
    uploadProgress.value = 0;
  }
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  saving.value = true;
  errors.value = {};

  try {
    const issueData = {
      title: formData.value.title.trim(),
      description: formData.value.description?.trim(),
      status: formData.value.status,
      priority: formData.value.priority,
      createdAt: formData.value.createdAt,
      resolvedDate: formData.value.resolvedDate || undefined,
      assignedTo: formData.value.assignedTo || undefined,
      resolutionNotes: formData.value.resolutionNotes?.trim() || undefined
    };

    let result;
    if (isEditing.value && props.issue) {
      result = await deliveryIssuesService.updateIssue(
        props.projectId.toString(),
        props.issue.id.toString(),
        issueData
      );
    } else {
      result = await deliveryIssuesService.createIssue(
        props.projectId.toString(),
        issueData
      );
    }

    if (result) {
      // Upload photos if any are selected
      if (selectedPhotos.value.length > 0) {
        try {
          await uploadPhotos(result.id);
        } catch (photoError) {
          // Issue was created but photos failed - notify user but don't fail the whole operation
          console.error('Photos upload failed after issue creation:', photoError);
        }
      }

      emit('issue-saved', result, !isEditing.value);
      emit('close');
    }
  } catch (error: any) {
    console.error('Error saving issue:', error);
    errors.value.general = error.message || t('issues.errors.saveFailed');
    notifyError(errors.value.general);
  } finally {
    saving.value = false;
  }
};

// Watchers
watch(() => props.visible, (newVal: boolean) => {
  if (newVal) {
    loadFormData();
  }
});

// Lifecycle
onMounted(() => {
  if (props.visible) {
    loadFormData();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AppModal, {
    show: _ctx.visible,
    title: isEditing.value ? _ctx.$t('issues.editIssue') : _ctx.$t('issues.addIssue'),
    icon: isEditing.value ? 'edit' : 'plus',
    size: "large",
    onClose: _cache[9] || (_cache[9] = ($event: any) => (_ctx.$emit('close')))
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_51, [
        _createVNode(AppButton, {
          type: "button",
          variant: "secondary",
          onClick: _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('close'))),
          disabled: saving.value,
          class: "issue-form__btn issue-form__btn--cancel"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('common.cancel')), 1)
          ]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(AppButton, {
          type: "button",
          variant: "primary",
          onClick: handleSubmit,
          loading: saving.value,
          disabled: saving.value || !isFormValid.value,
          class: "issue-form__btn issue-form__btn--save"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(isEditing.value ? _unref(t)('common.save') : _unref(t)('common.create')), 1)
          ]),
          _: 1
        }, 8, ["loading", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("form", {
          onSubmit: _withModifiers(handleSubmit, ["prevent"])
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("label", _hoisted_4, [
                _cache[10] || (_cache[10] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "18",
                  height: "18",
                  class: "app-form-field__icon"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                  })
                ], -1)),
                _createTextVNode(" " + _toDisplayString(_unref(t)('issues.title')), 1)
              ]),
              _createElementVNode("div", _hoisted_5, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.title) = $event)),
                  type: "text",
                  class: _normalizeClass(["app-form-field__input", { 'app-form-field__input--error': errors.value.title }]),
                  placeholder: _unref(t)('issues.placeholders.enterTitle'),
                  maxlength: "100",
                  required: ""
                }, null, 10, _hoisted_6), [
                  [_vModelText, formData.value.title]
                ])
              ]),
              (errors.value.title)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _cache[11] || (_cache[11] = _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      width: "16",
                      height: "16"
                    }, [
                      _createElementVNode("path", {
                        fill: "currentColor",
                        d: "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"
                      })
                    ], -1)),
                    _createTextVNode(" " + _toDisplayString(errors.value.title), 1)
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("label", _hoisted_9, [
                _cache[12] || (_cache[12] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "18",
                  height: "18",
                  class: "app-form-field__icon"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"
                  })
                ], -1)),
                _createTextVNode(" " + _toDisplayString(_unref(t)('issues.description')), 1)
              ]),
              _createElementVNode("div", _hoisted_10, [
                _withDirectives(_createElementVNode("textarea", {
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.description) = $event)),
                  class: "app-form-field__input app-form-field__input--textarea",
                  placeholder: _unref(t)('issues.placeholders.enterDescription'),
                  rows: "4",
                  maxlength: "1000"
                }, null, 8, _hoisted_11), [
                  [_vModelText, formData.value.description]
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("label", _hoisted_15, [
                    _cache[13] || (_cache[13] = _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      width: "18",
                      height: "18",
                      class: "app-form-field__icon"
                    }, [
                      _createElementVNode("path", {
                        fill: "currentColor",
                        d: "M12,2L15.09,8.26L22,9.27L17,14.14L18.18,21.02L12,17.77L5.82,21.02L7,14.14L2,9.27L8.91,8.26L12,2Z"
                      })
                    ], -1)),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('issues.priority')), 1)
                  ]),
                  _createElementVNode("div", _hoisted_16, [
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.priority) = $event)),
                      class: "app-form-field__input app-form-field__input--select",
                      required: ""
                    }, [
                      _createElementVNode("option", _hoisted_17, _toDisplayString(_unref(t)('issues.priorities.LOW')), 1),
                      _createElementVNode("option", _hoisted_18, _toDisplayString(_unref(t)('issues.priorities.MEDIUM')), 1),
                      _createElementVNode("option", _hoisted_19, _toDisplayString(_unref(t)('issues.priorities.HIGH')), 1),
                      _createElementVNode("option", _hoisted_20, _toDisplayString(_unref(t)('issues.priorities.CRITICAL')), 1)
                    ], 512), [
                      [_vModelSelect, formData.value.priority]
                    ])
                  ])
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_21, [
              _createElementVNode("div", _hoisted_22, [
                _createElementVNode("div", _hoisted_23, [
                  _createElementVNode("label", _hoisted_24, [
                    _cache[14] || (_cache[14] = _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      width: "18",
                      height: "18",
                      class: "app-form-field__icon"
                    }, [
                      _createElementVNode("path", {
                        fill: "currentColor",
                        d: "M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"
                      })
                    ], -1)),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('issues.assignedTo')), 1)
                  ]),
                  _createElementVNode("div", _hoisted_25, [
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.assignedTo) = $event)),
                      class: "app-form-field__input app-form-field__input--select"
                    }, [
                      _createElementVNode("option", _hoisted_26, _toDisplayString(_unref(t)('issues.placeholders.selectIntervenant')), 1),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.intervenants, (intervenant) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: intervenant.id,
                          value: intervenant.id
                        }, _toDisplayString(intervenant.name), 9, _hoisted_27))
                      }), 128))
                    ], 512), [
                      [_vModelSelect, formData.value.assignedTo]
                    ])
                  ])
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_28, [
              _createElementVNode("div", _hoisted_29, [
                (showResolvedDate.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                      _createElementVNode("label", _hoisted_31, [
                        _cache[15] || (_cache[15] = _createElementVNode("svg", {
                          viewBox: "0 0 24 24",
                          width: "18",
                          height: "18",
                          class: "app-form-field__icon"
                        }, [
                          _createElementVNode("path", {
                            fill: "currentColor",
                            d: "M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"
                          })
                        ], -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(t)('issues.resolvedDate')), 1)
                      ]),
                      _createElementVNode("div", _hoisted_32, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.value.resolvedDate) = $event)),
                          type: "date",
                          class: "app-form-field__input app-form-field__input--date",
                          min: formData.value.resolvedDate
                        }, null, 8, _hoisted_33), [
                          [_vModelText, formData.value.resolvedDate]
                        ])
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ])
            ]),
            (showResolutionNotes.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
                  _createElementVNode("label", {
                    class: _normalizeClass(["app-form-field__label", { 'app-form-field__label--required': formData.value.status === 'RESOLVED' }])
                  }, [
                    _cache[16] || (_cache[16] = _createElementVNode("svg", {
                      viewBox: "0 0 24 24",
                      width: "18",
                      height: "18",
                      class: "app-form-field__icon"
                    }, [
                      _createElementVNode("path", {
                        fill: "currentColor",
                        d: "M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"
                      })
                    ], -1)),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('issues.resolutionNotes')), 1)
                  ], 2),
                  _createElementVNode("div", _hoisted_35, [
                    _withDirectives(_createElementVNode("textarea", {
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.resolutionNotes) = $event)),
                      class: "app-form-field__input app-form-field__input--textarea",
                      placeholder: _unref(t)('issues.placeholders.enterResolutionNotes'),
                      rows: "3",
                      maxlength: "500"
                    }, null, 8, _hoisted_36), [
                      [_vModelText, formData.value.resolutionNotes]
                    ])
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_37, [
              _createElementVNode("label", _hoisted_38, [
                _cache[17] || (_cache[17] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "18",
                  height: "18",
                  class: "app-form-field__icon"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z"
                  })
                ], -1)),
                _createTextVNode(" " + _toDisplayString(_unref(t)('issues.photos')), 1)
              ]),
              _createElementVNode("div", {
                class: _normalizeClass(["photo-upload-area", { 'photo-upload-area--dragging': isDragging.value }]),
                onClick: triggerFileInput,
                onDragover: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (isDragging.value = true), ["prevent"])),
                onDragleave: _cache[7] || (_cache[7] = _withModifiers(($event: any) => (isDragging.value = false), ["prevent"])),
                onDrop: _withModifiers(handleFileDrop, ["prevent"])
              }, [
                _createElementVNode("input", {
                  ref_key: "fileInput",
                  ref: fileInput,
                  type: "file",
                  accept: "image/*",
                  multiple: "",
                  class: "photo-upload-input",
                  onChange: handleFileSelect
                }, null, 544),
                _cache[18] || (_cache[18] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "32",
                  height: "32",
                  class: "photo-upload-icon"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"
                  })
                ], -1)),
                _createElementVNode("p", _hoisted_39, _toDisplayString(_unref(t)('issues.uploadPhotos')), 1),
                _createElementVNode("p", _hoisted_40, _toDisplayString(_unref(t)('issues.uploadPhotosHint')), 1)
              ], 34),
              (selectedPhotos.value.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_41, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedPhotos.value, (photo, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: index,
                        class: "photo-preview-item"
                      }, [
                        _createElementVNode("img", {
                          src: photo.preview,
                          alt: photo.file.name,
                          class: "photo-preview-image"
                        }, null, 8, _hoisted_42),
                        _createElementVNode("div", _hoisted_43, [
                          _createElementVNode("span", _hoisted_44, _toDisplayString(photo.file.name), 1),
                          _createElementVNode("span", _hoisted_45, _toDisplayString(formatFileSize(photo.file.size)), 1)
                        ]),
                        _createElementVNode("button", {
                          type: "button",
                          class: "photo-preview-remove",
                          onClick: _withModifiers(($event: any) => (removePhoto(index)), ["stop"]),
                          title: _unref(t)('common.remove')
                        }, [...(_cache[19] || (_cache[19] = [
                          _createElementVNode("svg", {
                            viewBox: "0 0 24 24",
                            width: "16",
                            height: "16"
                          }, [
                            _createElementVNode("path", {
                              fill: "currentColor",
                              d: "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                            })
                          ], -1)
                        ]))], 8, _hoisted_46)
                      ]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              (uploadingPhotos.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_47, [
                    _createElementVNode("div", _hoisted_48, [
                      _createElementVNode("div", {
                        class: "upload-progress-fill",
                        style: _normalizeStyle({ width: `${uploadProgress.value}%` })
                      }, null, 4)
                    ]),
                    _createElementVNode("span", _hoisted_49, _toDisplayString(_unref(t)('issues.uploadingPhotos', { progress: uploadProgress.value })), 1)
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          (errors.value.general)
            ? (_openBlock(), _createElementBlock("div", _hoisted_50, [
                _cache[20] || (_cache[20] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "20",
                  height: "20"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"
                  })
                ], -1)),
                _createElementVNode("span", null, _toDisplayString(errors.value.general), 1)
              ]))
            : _createCommentVNode("", true)
        ], 32)
      ])
    ]),
    _: 1
  }, 8, ["show", "title", "icon"]))
}
}

})